import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentApplication } from '../useCurrentApplication'
import { formatPermissionResourceLabel } from '../AppPermissionsPageV2/permissionResourceLabels'

// Shadcn components
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Switch } from '@/components/shadcn/switch'
import { Button } from '@/components/shadcn/button'

// Form validation schema
const formSchema = z.object({
  roleId: z.number().min(1, 'Role is required'),
  // Reuse this field to select Data Asset (da_), File Template (ft_), Collaboration Channel (cc_), or Pipeline (pl_)
  fileTemplateIdOrDataAssetId: z.string().min(1, 'Resource is required'),
  // Data Asset / File Template permissions
  allowRead: z.boolean(),
  allowUpload: z.boolean(),
  allowDownloadDraft: z.boolean(),
  allowDelete: z.boolean(),
  allowComment: z.boolean(),
  allowAccept: z.boolean(),
  allowDiscard: z.boolean(),
  // Collaboration Channel permissions
  canCreateThread: z.boolean(),
  canComment: z.boolean(),
  canAssignThread: z.boolean(),
  canResolveThread: z.boolean(),
  canDeleteMessage: z.boolean(),
  // Pipeline permissions
  canExecute: z.boolean(),
  canView: z.boolean(),
})

export type TApplicationPermissionsForm = z.infer<typeof formSchema>

type MessagesApplicationsNewDataAssetPermission = RestSchemas['Messages.Applications.NewDataAssetPermission']
type MessagesApplicationsNewFileTemplateRole = RestSchemas['Messages.Applications.NewFileTemplateRole']
type MessagesApplicationsRoleTemplateAssetPermissions =
  RestSchemas['Messages.Applications.RoleTemplateAssetPermissions']

type ApplicationPermissionsFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedAppPerm?: MessagesApplicationsRoleTemplateAssetPermissions
}

export const ApplicationPermissionsForm = (props: ApplicationPermissionsFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: ApplicationPermissionsFormProps) => {
  const edit = !!props.selectedAppPerm
  const { selectedAppPerm, onSubmit } = props

  const { applicationId } = useCurrentApplication()

  const {
    getApplicationsApiListApplicationRoles,
    getApplicationsApiGetRolePermissions,
    getDataAssetApiListDataAssets,
    getTemplatesApiListFileTemplates,
    getCollaborationApiGetCollaborationChannels,
    putCollaborationApiAddCollaborationPermission,
    postCollaborationApiUpdateCollaborationPermission,
    putApplicationsApiAddDataAssetPermission,
    postApplicationsApiUpdateDataAssetPermission,
    putApplicationsApiAddFileTemplateRole,
    postApplicationsApiUpdateFileTemplateRole,
    getPipelineApiGetPipeline,
    putPipelineApiAddPipelinePermission,
    getEntityTypeApiGetEntityTypesByApplicationId,
  } = useRestClient()

  const { data: rolePermissions } = getApplicationsApiGetRolePermissions.useSuspenseQuery({
    query: { applicationId },
  })

  const invalidateGetRolePermissions = useCallback(
    () =>
      getApplicationsApiGetRolePermissions.invalidateQueries({
        parameters: {
          query: { applicationId },
        },
      }),
    [applicationId, getApplicationsApiGetRolePermissions]
  )

  const { data: fileTemplates } = getTemplatesApiListFileTemplates.useSuspenseQuery({
    query: {
      applicationId,
    },
  })

  const { data: dataAssets } = getDataAssetApiListDataAssets.useSuspenseQuery({
    query: {
      applicationId,
    },
  })

  const { data: appRoles } = getApplicationsApiListApplicationRoles.useSuspenseQuery({
    query: {
      applicationId,
    },
  })

  const { data: collaborationChannels } = getCollaborationApiGetCollaborationChannels.useSuspenseQuery({
    path: { applicationId },
  })

  const { data: pipelines } = getPipelineApiGetPipeline.useSuspenseQuery({
    path: { applicationId },
  })

  const { data: entityTypes } = getEntityTypeApiGetEntityTypesByApplicationId.useSuspenseQuery({
    path: { applicationId },
  })

  const entityTypeNameById = useMemo(
    () => new Map(entityTypes.map((entityType) => [entityType.id, entityType.name])),
    [entityTypes]
  )

  const { mutate: addPipelinePermission } = putPipelineApiAddPipelinePermission.useMutation()
  const { mutate: addDataAssetPermission } = putApplicationsApiAddDataAssetPermission.useMutation()
  const { mutate: updateDataAssetPermission } = postApplicationsApiUpdateDataAssetPermission.useMutation()
  const { mutate: addFileTemplatePermission } = putApplicationsApiAddFileTemplateRole.useMutation()
  const { mutate: updateFileTemplatePermission } = postApplicationsApiUpdateFileTemplateRole.useMutation()
  const { mutate: addChannelPermission } = putCollaborationApiAddCollaborationPermission.useMutation()
  const { mutate: _updateChannelPermission } = postCollaborationApiUpdateCollaborationPermission.useMutation()

  // Parse existing permissions for edit mode from the selected row
  const allowed = selectedAppPerm ? JSON.parse(selectedAppPerm.permissions)[0] : {}
  const {
    allow_accept,
    allow_comment,
    allow_delete,
    allow_discard,
    allow_download_draft,
    allow_read,
    allow_upload,
    canCreateThread: can_create_thread,
    canComment: can_comment,
    canAssignThread: can_assign_thread,
    canResolveThread: can_resolve_thread,
    canDeleteMessage: can_delete_message,
    id: permissionIdForChannel,
  } = allowed as unknown as Record<string, unknown>

  const { roleId, resource, id } = selectedAppPerm || {}

  const defaultValues = useMemo(() => {
    if (edit) {
      return {
        roleId: roleId,
        fileTemplateIdOrDataAssetId:
          resource === 'assets' ? `da_${id}` : resource === 'template' ? `ft_${id}` : `cc_${id}`,
        // Data Asset / File Template defaults
        allowRead: !!allow_read,
        allowUpload: !!allow_upload,
        allowDownloadDraft: !!allow_download_draft,
        allowDelete: !!allow_delete,
        allowComment: !!allow_comment,
        allowAccept: !!allow_accept,
        allowDiscard: !!allow_discard,
        // Channel defaults
        canCreateThread: !!can_create_thread,
        canComment: !!can_comment,
        canAssignThread: !!can_assign_thread,
        canResolveThread: !!can_resolve_thread,
        canDeleteMessage: !!can_delete_message,
      }
    }
    return {
      // roleId: 0,
      // fileTemplateIdOrDataAssetId: '',
      allowRead: true,
      allowUpload: false,
      allowDownloadDraft: false,
      allowDelete: false,
      allowComment: true,
      allowAccept: false,
      allowDiscard: true,
      canCreateThread: false,
      canComment: true,
      canAssignThread: false,
      canResolveThread: false,
      canDeleteMessage: false,
      canExecute: false,
      canView: true,
    }
  }, [
    allow_accept,
    allow_comment,
    allow_delete,
    allow_discard,
    allow_download_draft,
    allow_read,
    allow_upload,
    edit,
    id,
    resource,
    roleId,
    can_create_thread,
    can_comment,
    can_assign_thread,
    can_resolve_thread,
    can_delete_message,
  ])

  const form = useForm<TApplicationPermissionsForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const watchFileTemplateIdOrDataAssetId = useWatch({
    control: form.control,
    name: 'fileTemplateIdOrDataAssetId',
  })

  const watchRoleId = useWatch({
    control: form.control,
    name: 'roleId',
  })

  // Dynamic boolean fields based on selection
  const booleanFields = useMemo(() => {
    if (!watchFileTemplateIdOrDataAssetId || watchFileTemplateIdOrDataAssetId === 'None') {
      return []
    }

    // Collaboration channel permissions
    if (watchFileTemplateIdOrDataAssetId?.startsWith('cc_')) {
      const channelFields: Array<{ name: keyof TApplicationPermissionsForm; label: string }> = [
        { name: 'canCreateThread', label: 'Create Thread' },
        { name: 'canComment', label: 'Comment' },
        { name: 'canAssignThread', label: 'Assign Thread' },
        { name: 'canResolveThread', label: 'Resolve Thread' },
        { name: 'canDeleteMessage', label: 'Delete Message' },
      ]
      return channelFields
    }

    // Pipeline permissions
    if (watchFileTemplateIdOrDataAssetId?.startsWith('pl_')) {
      return [
        { name: 'canExecute' as keyof TApplicationPermissionsForm, label: 'Execute' },
        { name: 'canView' as keyof TApplicationPermissionsForm, label: 'View' },
      ]
    }

    // Data Asset / File Template permissions
    const baseFields: Array<{ name: keyof TApplicationPermissionsForm; label: string }> = [
      { name: 'allowRead', label: 'Read' },
      { name: 'allowUpload', label: 'Upload' },
      { name: 'allowDelete', label: 'Delete' },
      { name: 'allowComment', label: 'Comment' },
      { name: 'allowAccept', label: 'Accept' },
      { name: 'allowDiscard', label: 'Discard' },
    ]

    // Add download draft field only for data assets
    if (watchFileTemplateIdOrDataAssetId?.startsWith('da_')) {
      baseFields.splice(2, 0, { name: 'allowDownloadDraft', label: 'Allow Download Draft' })
    }

    return baseFields
  }, [watchFileTemplateIdOrDataAssetId])

  const unavailableOptions = useMemo(() => {
    return rolePermissions.map((x) => {
      const item = { roleId: x.roleId, ftOrDaKey: x.resource === 'assets' ? `da_${x.id}` : `ft_${x.id}` }
      return item
    })
  }, [rolePermissions])
  console.log(unavailableOptions)

  const { reset: _reset } = form
  const handleSubmit = useCallback(
    async (data: Required<TApplicationPermissionsForm>) => {
      let roleType: 'da' | 'ft' | 'cc' | 'pl' = 'da'
      let dataAssetId = 0
      let fileTemplateId = 0
      let collaborationChannelId = 0
      let pipelineId = 0
      const channelPermissionId = permissionIdForChannel ? parseInt(String(permissionIdForChannel)) : 0

      if (data.fileTemplateIdOrDataAssetId.startsWith('da_')) {
        roleType = 'da'
        dataAssetId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
        fileTemplateId = 0
        // entityId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
      } else if (data.fileTemplateIdOrDataAssetId.startsWith('ft_')) {
        roleType = 'ft'
        dataAssetId = 0
        fileTemplateId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
        // entityId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
      } else if (data.fileTemplateIdOrDataAssetId.startsWith('cc_')) {
        roleType = 'cc'
        collaborationChannelId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
      } else if (data.fileTemplateIdOrDataAssetId.startsWith('pl_')) {
        roleType = 'pl'
        pipelineId = parseInt(data.fileTemplateIdOrDataAssetId.substring(3))
      } else {
        toast.warning('Missing resource selection')
        return
      }

      if (!data.roleId) {
        toast.warning('Missing role')
        return
      }

      if (
        !edit &&
        unavailableOptions &&
        unavailableOptions.some(
          (option) => option.roleId === data.roleId && option.ftOrDaKey === data.fileTemplateIdOrDataAssetId
        )
      ) {
        toast.warning(
          'Already a config for selected Role and Data Asset / File Template. Edit existing config instead.'
        )
        return
      }

      const permissionData = {
        roleId: data.roleId,
        allowRead: data.allowRead,
        allowUpload: data.allowUpload,
        allowDownloadDraft: data.allowDownloadDraft,
        allowDelete: data.allowDelete,
        allowComment: data.allowComment,
        allowAccept: data.allowAccept,
        allowDiscard: data.allowDiscard,
      }

      if (roleType === 'da') {
        const daPermission: MessagesApplicationsNewDataAssetPermission = {
          ...permissionData,
          dataAssetId,
        }

        const mutate = edit ? updateDataAssetPermission : addDataAssetPermission

        mutate(
          { body: daPermission },
          {
            onSuccess: () => {
              toast.success(`${edit ? 'Updated' : 'Added'} data asset permission`)
              onSubmit?.()
              invalidateGetRolePermissions()
              // reset(data)
            },
            onError: () => {
              toast.error(`Error ${edit ? 'updating' : 'adding'} data asset permission`)
            },
          }
        )
      } else if (roleType === 'ft') {
        const ftPermission: MessagesApplicationsNewFileTemplateRole = {
          ...permissionData,
          fileTemplateId,
        }

        const mutate = edit ? updateFileTemplatePermission : addFileTemplatePermission

        mutate(
          { body: ftPermission },
          {
            onSuccess: () => {
              toast.success(`${edit ? 'Updated' : 'Added'} file template permission`)
              onSubmit?.()
              invalidateGetRolePermissions()
              // reset(data)
            },
            onError: () => {
              toast.error(`Error ${edit ? 'updating' : 'adding'} file template permission`)
            },
          }
        )
      } else if (roleType === 'pl') {
        addPipelinePermission(
          {
            path: { applicationId, pipelineId },
            body: {
              pipelineId,
              roleId: data.roleId,
              canExecute: data.canExecute,
              canView: data.canView,
            },
          },
          {
            onSuccess: () => {
              toast.success('Added pipeline permission')
              onSubmit?.()
              invalidateGetRolePermissions()
            },
            onError: () => toast.error('Error adding pipeline permission'),
          }
        )
      } else if (roleType === 'cc') {
        const channelPermission: RestSchemas['Messages.Collaboration.NewCollaborationPermission'] = {
          collaborationChannelId,
          roleId: data.roleId,
          canCreateThread: data.canCreateThread,
          canComment: data.canComment,
          canAssignThread: data.canAssignThread,
          canResolveThread: data.canResolveThread,
          canDeleteMessage: data.canDeleteMessage,
        }

        if (edit && channelPermissionId) {
          const mutate = postCollaborationApiUpdateCollaborationPermission.useMutation().mutate

          mutate(
            {
              path: {
                applicationId,
                channelId: collaborationChannelId,
                permissionId: channelPermissionId,
              },
              body: channelPermission as RestSchemas['Messages.Collaboration.UpdateCollaborationPermission'],
            },
            {
              onSuccess: () => {
                toast.success('Updated collaboration channel permission')
                onSubmit?.()
              },
              onError: () => {
                toast.error('Error updating collaboration channel permission')
              },
            }
          )
        } else {
          const mutate = addChannelPermission
          mutate(
            { path: { applicationId, channelId: collaborationChannelId }, body: channelPermission },
            {
              onSuccess: () => {
                toast.success('Added collaboration channel permission')
                onSubmit?.()
              },
              onError: () => {
                toast.error('Error adding collaboration channel permission')
              },
            }
          )
        }
      }
    },
    [
      addDataAssetPermission,
      addFileTemplatePermission,
      addChannelPermission,
      edit,
      invalidateGetRolePermissions,
      onSubmit,
      unavailableOptions,
      updateDataAssetPermission,
      updateFileTemplatePermission,
      applicationId,
      permissionIdForChannel,
      postCollaborationApiUpdateCollaborationPermission,
    ]
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="roleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      defaultValue={field.value?.toString()}
                      disabled={edit}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {appRoles?.map((role) => (
                          <SelectItem key={role.id.toString()} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="fileTemplateIdOrDataAssetId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Template / Data Asset / Collaboration Channel</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={edit}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a file template, data asset or channel" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {dataAssets?.map((asset) => {
                          const key = `da_${asset.id}`
                          const isDisabled = unavailableOptions?.some(
                            (x) => x.roleId === watchRoleId && x.ftOrDaKey === key
                          )
                          return (
                            <SelectItem key={key} value={key} disabled={isDisabled}>
                              {formatPermissionResourceLabel(
                                'Data Asset',
                                asset.name,
                                entityTypeNameById.get(asset.entityTypeId)
                              )}
                            </SelectItem>
                          )
                        })}
                        {fileTemplates?.map((template) => {
                          const key = `ft_${template.id}`
                          const isDisabled = unavailableOptions?.some(
                            (x) => x.roleId === watchRoleId && x.ftOrDaKey === key
                          )
                          return (
                            <SelectItem key={key} value={key} disabled={isDisabled}>
                              {formatPermissionResourceLabel(
                                'File Template',
                                template.name,
                                entityTypeNameById.get(template.entityTypeId)
                              )}
                            </SelectItem>
                          )
                        })}
                        {collaborationChannels?.map((channel) => {
                          const key = `cc_${channel.id}`
                          return (
                            <SelectItem key={key} value={key}>
                              Collaboration Channel: {channel.value || channel.key}
                            </SelectItem>
                          )
                        })}
                        {pipelines?.map((pipeline) => {
                          const key = `pl_${pipeline.id}`
                          return (
                            <SelectItem key={key} value={key}>
                              Pipeline: {pipeline.name}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!!booleanFields.length && (
                <div className="space-y-2 rounded border bg-background p-5">
                  <div className="text-lg font-medium">Allowed permissions:</div>
                  {booleanFields.map((field) => (
                    <FormField
                      key={field.name}
                      control={form.control}
                      name={field.name}
                      render={({ field: formField }) => (
                        <FormItem className="bg-offwhite flex flex-row items-center justify-between rounded-lg px-4 py-2">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">{field.label}</FormLabel>
                          </div>
                          <FormControl>
                            <Switch checked={!!formField.value} onCheckedChange={formField.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="form-action-container">
              <Button type="submit">Submit</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
